import { ActivatedRoute } from '@angular/router';
import { RoutingService } from '../../../routing/routing.service';
import { RouteChangeParams } from '../../../routing/route-change-params';
import { TokenService } from '../../../auth/token.service';
import { Message, OnlineService, TabSocket, UUIDHelper, UpdateService } from '@nts/std/utility';
import { ToastMessageService } from '../toast-message/toast-message.service';
import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, OnInit, ViewContainerRef, ChangeDetectionStrategy } from '@angular/core';
import { BehaviorSubject, filter, Observable, Subject } from 'rxjs';
import { LogService } from '@nts/std/utility';
import { UIStarter } from '../../../starter/ui-starter';
import { AuthService } from '../../../auth/auth.service';
import { UnauthorizedRequestComponent } from '../../errors/unauthorized-request/unauthorized-request.component';
import { InternalServerErrorComponent } from '../../errors/internal-server-error/internal-server-error.component';
import { NotFoundComponent } from '../../errors/not-found/not-found.component';
import { OfflineModeComponent } from '../../errors/offline-mode/offline-mode.component';
import { CompanyNotFoundComponent } from '../../errors/company-not-found/company-not-found.component';
import { EnterpriseNotFoundComponent } from '../../errors/enterprise-not-found/enterprise-not-found.component';
import { EnterpriseListNotFoundComponent } from '../../errors/enterprise-list-not-found/enterprise-list-not-found.component';
import { ClassConstructor } from '@nts/std/serialization';
import { ComponentErrorInterface } from '../../errors/component-error.interface';
import { TelemetryService } from '@nts/std/telemetry';
import { EnvironmentConfiguration } from '@nts/std/environments';
import { CurrentRouteService } from '../../../routing/current-route.service';
import { MessageCodes } from '../../../resources/message-codes';
import { BadRequestComponent } from '../../errors/bad-request/bad-request.component';
import { AuthQueryParams, MessageType, ToastMessageType } from '@nts/std/types';
import type { BaseErrorInterface, StatusMessageInterface } from '@nts/std/interfaces';
import { UntilDestroy } from '@ngneat/until-destroy';
import { VersionEvent } from '@angular/service-worker';
import { BaseError } from '@nts/std/messages';

export interface LoadOutletComponent<TOutletComponentType = PendingChangesAwareInterface> {
  outletComponent: TOutletComponentType;
  outletComponentChanged$: Subject<TOutletComponentType>;
  onOutletLoaded(component: TOutletComponentType): void;
}

export interface PendingChangesAwareInterface {
  handlePendingChanges(): Promise<boolean>;
}
@UntilDestroy()
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export abstract class BaseContainerComponent<TOutletComponentType extends PendingChangesAwareInterface = PendingChangesAwareInterface> implements AfterViewInit, OnInit, PendingChangesAwareInterface, LoadOutletComponent<TOutletComponentType> {

  isLoading$ = new BehaviorSubject<boolean>(true);
  inIframe = false;
  showHeader = false;

  /**
   * utilizza il document placeholder al posto del router outlet, nel caso in cui è necessario renderizzare template custom ad esempio errori
   */
  usePlaceHolder = true;
  isOnline$: BehaviorSubject<boolean>;

  isDetached$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Sono presenti errori?
   */
  hasErrors = false;

  /**
   * placeholder utilizzato per renderizzare template custom
   */
  abstract documentPlaceHolder: ViewContainerRef;

  protected unloadHandlerEnabled = true;
  protected tabMessagesSocket: TabSocket = null;

  outletComponent: TOutletComponentType = null;
  outletComponentChanged$ = new Subject<TOutletComponentType>();

  constructor(
    protected readonly routingService: RoutingService,
    protected readonly activatedRoute: ActivatedRoute,
    // Necessario per avviarlo e gesitre i token in tutte le maschere
    protected readonly tokenService: TokenService,
    protected readonly toastMessageService: ToastMessageService,
    protected readonly updateService: UpdateService,
    protected readonly onlineService: OnlineService,
    protected readonly authService: AuthService,
    protected readonly telemetryService: TelemetryService,
    protected readonly environmentConfiguration: EnvironmentConfiguration,
    protected readonly cd: ChangeDetectorRef,
    protected readonly currentRouteService: CurrentRouteService
  ) {
    // Codice comune per tutti i container

    this.isOnline$ = onlineService.isOnline$;
  }

  ngOnAttach() {
    this.isDetached$.next(false);
  }

  ngOnDetach() {
    this.isDetached$.next(true);
  }

  ngOnInit(): void {

    // Codice comune per tutti i container
    this.inIframe = this.routingService.inIframe;
    this.showHeader = !this.inIframe;

    if (window.location.href.includes('show-header=true')) {
      this.showHeader = true;
    }

    if (this.inIframe) {
      this.tabMessagesSocket = new TabSocket(
        UIStarter.zone,
        window.opener as Window || window.parent
      );
      this.handleTabMessageCommunication(this.tabMessagesSocket);
      this.setupHealthCheckListener(this.tabMessagesSocket);
    }

    const isExternalReturn = window.location.href.includes('external-return=');
    if (isExternalReturn) {
      if (!this.tabMessagesSocket) {
        this.tabMessagesSocket = new TabSocket(
          UIStarter.zone,
          window.opener as Window || window.parent
        );
        this.handleTabMessageCommunication(this.tabMessagesSocket);
        this.setupHealthCheckListener(this.tabMessagesSocket);
      }
    }

    this.routingService.routeChangeRequested.pipe(this.untilDestroyed()).subscribe(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async (routeChangeParams: RouteChangeParams) => {
        if (this.inIframe) {
          await this.handleNavigationInsideIframe(routeChangeParams);
        } else {
          await this.handleNavigationOutsideIframe(routeChangeParams);
        }
      }
    );

    this.updateService.updateNotification.pipe(this.untilDestroyed()).subscribe(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async (evt) => {
        await this.handleUpdateNotification(evt);
      }
    );

    this.routingService.onTabMessageReceived.pipe(this.untilDestroyed()).subscribe(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async (payload: { message: Message<string>, action: (payload: any) => void; }) => {
        await this.handleTabMessageReceived(payload);
      }
    );
  }

  /**
   * Implementare la (activate)="onOutletLoaded($event)" nel template per gestire le modifiche in sospeso dal container
   *
   * @returns se esistono modifiche in sospeso
   */
  async handlePendingChanges(): Promise<boolean> {
    if (this.outletComponent && this.outletComponent.handlePendingChanges) {
      return this.outletComponent.handlePendingChanges();
    } else {
      return false;
    }
  }

  onOutletLoaded(component: TOutletComponentType) {
    this.outletComponent = component;
    this.outletComponentChanged$.next(component);
  }

  async checkToken(): Promise<boolean> {
    return await this.handleTokenIdParam();
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async ngAfterViewInit(): Promise<void> {
    if (!await this.checkToken()) {
      return;
    }
    this.hasErrors = await this.checkEnterpriseError();
    this.usePlaceHolder = this.hasErrors;
    this.cd.detectChanges();
  }

  async checkEnterpriseError(): Promise<boolean> {
    if (this.currentRouteService.isAuthenticatedRoute) {
      const enterpriseDataError = await this.authService.getEnterpriseDataError();
      this.isLoading$.next(false);
      if (enterpriseDataError) {
        await this.renderErrorComponentFromErrors([enterpriseDataError]);
        return true;
      }
    }
    return false;
  }

  async handleTokenIdParam(): Promise<boolean> {

    const tenantId: string = this.activatedRoute.parent.snapshot.queryParams[AuthQueryParams.TENANT_ID_QUERY_KEY] as string;
    if (tenantId != null) {
      const accessToken = await this.authService.getAccessToken();
      const decoded = this.authService.decodeToken(accessToken);
      if (decoded.Tnt != tenantId) {
        await this.authService.changeTenant({
          tenantId,
          includeCurrentUrlInAuthRedirect: true
        });
        return false;
      }
    }
    return true;
  }

  track(error: BaseErrorInterface) {
    const err = new Error(error.description);
    err.stack = error?.stackTrace;

    if (
      this.telemetryService.initialized && error.uuid && (
        error.code === 'DefaultError' || error.code === 'InternalServerError'
      )
    ) {
      this.telemetryService.trackException({
        id: error.uuid,
        exception: err,
        properties: {
          uuid: error.uuid,
          error
        }
      });
      if (this.environmentConfiguration.production) {
        LogService.error(err.toString());
      }
      LogService.error('Il tuo errore è stato tracciato, questo è il codice univoco generato per questo errore: ' + error?.uuid);
    } else {
      // TODO tracciare altre pagine di errore??
    }
  }

  getComponentErrorMapping() {
    return {
      [MessageCodes.UnauthorizedHttpStatusCode]: UnauthorizedRequestComponent,
      [MessageCodes.InternalServerErrorHttpStatusCode]: InternalServerErrorComponent,
      [MessageCodes.BadRequestHttpStatusCode]: BadRequestComponent,
      [MessageCodes.NotFoundHttpStatusCode]: NotFoundComponent,
      Forbidden: UnauthorizedRequestComponent,
      OfflineMode: OfflineModeComponent,
      CompanyNotFound: CompanyNotFoundComponent,
      EnterpriseNotFound: EnterpriseNotFoundComponent,
      EnterpriseListNotFound: EnterpriseListNotFoundComponent,
      DefaultError: InternalServerErrorComponent,
    };
  }

  async renderErrorComponentFromErrors(errors: BaseErrorInterface[]): Promise<void> {

    if (this.documentPlaceHolder?.clear) {
      this.documentPlaceHolder?.clear();


      const defaultError: BaseErrorInterface = new BaseError();
      defaultError.code = 'DefaultError';
      defaultError.description = 'Errore imprevisto';

      const computedErrors: BaseErrorInterface[] = errors?.length > 0 ? errors : [defaultError];

      const componentErrorMapping = this.getComponentErrorMapping();

      let componentError: ClassConstructor<ComponentErrorInterface>;

      const forbiddenErrors = computedErrors.filter((e) =>
        e.code === MessageCodes.UnauthorizedHttpStatusCode || e.description === 'Forbidden'
      );

      if (forbiddenErrors?.length > 0) {
        componentError = UnauthorizedRequestComponent;
      } else {
        // se non esiste il componente per gestire il codice di errore
        if (componentErrorMapping[computedErrors[0].code] == null) {
          // aggiungo il codice di default
          computedErrors.unshift(defaultError);
        }
        componentError = componentErrorMapping[computedErrors[0].code];
      }

      const componentRef: ComponentRef<any> = this.documentPlaceHolder.createComponent(componentError);
      const componentInstance = componentRef.instance as ComponentErrorInterface;

      computedErrors[0].uuid = UUIDHelper.generateUUID();
      componentInstance.errors = computedErrors;
      this.track(computedErrors[0]);

      this.cd.detectChanges();

    }
    LogService.debug('renderErrorComponentFromErrors', errors);
    return Promise.resolve();
  }

  protected async handleTabMessageReceived(payload: {
    message: Message<string>,
    action: (payload: any, messageType: MessageType) => void;
  }) {
    switch (payload.message?.type) {
      case MessageType.AskPendingChanges: {
        // Disabilito l'unload handler
        this.unloadHandlerEnabled = false;
        const hasChanges = await this.handlePendingChanges();
        payload.action(hasChanges, MessageType.AnswerPendingChanges);
        break;
      }

      case MessageType.AskStatus: {
        // Disabilito l'unload handler
        this.unloadHandlerEnabled = false;
        const status = await this.checkStatus();
        payload.action(status, MessageType.AnswerStatus);
        break;
      }

      default: {
        break;
      }
    }
  }

  protected handleTabMessageCommunication(socket: TabSocket) {
    socket.onMessage<string>()
      .pipe(
        this.untilDestroyed(),
        filter((message: Message<string>) => message.type !== MessageType.HealthCheck)
      )
      .subscribe((message: Message<string>) => {
        // Wrap in Message object for backward compatibility
        message = { data: message.data, type: message.type, uuid: message.uuid?.length > 0 ? message.uuid : UUIDHelper.generateUUID() } as Message<string>;
        const commandMessageAction = (responseData: any, messageType = MessageType.CustomMessage) => {
          socket.send(responseData, messageType, message.uuid);
        };

        this.routingService.onTabMessageReceived.next({ message, action: commandMessageAction });
      });

    socket.sendCustomMessage({ timestamp: Date.now() }, MessageType.HealthCheck);
  }

  /**
   * Listen for health check messages and respond
   * Used to validate connection is still active
   */
  protected setupHealthCheckListener(socket: TabSocket) {
    socket.onMessage<{ timestamp: number; }>(MessageType.HealthCheck)
      .pipe(this.untilDestroyed())
      .subscribe((message: Message<{ timestamp: number; }>) => {
        // Respond to health check
        socket.sendCustomMessage({ timestamp: Date.now(), ack: true }, MessageType.HealthCheck);
        LogService.debug('Health check received, sending response');
      });
  }

  protected async handleNavigationInsideIframe(routeChangeParams: RouteChangeParams): Promise<void> {
    // se sono in un iframe non esiste windows opener ma windows parent
    const socket = new TabSocket(
      UIStarter.zone,
      window.opener as Window || window.parent,
      'navigator'
    );
    socket.send(routeChangeParams);
    return Promise.resolve();
  }

  protected async handleNavigationOutsideIframe(routeChangeParams: RouteChangeParams): Promise<void> {
    // Potrebbe essere richiamato al posto della router navigate
    return Promise.resolve();
  }

  protected async handleUpdateNotification(evt: VersionEvent): Promise<void> {
    switch (evt.type) {
      case 'VERSION_DETECTED':
        LogService.debug(`Downloading new app version: ${evt.version.hash}`);
        break;
      case 'VERSION_READY':
        LogService.debug(`Current app version: ${evt.currentVersion.hash}`);
        LogService.debug(`New app version ready for use: ${evt.latestVersion.hash}`);
        setTimeout(() => {
          this.toastMessageService.showUpdateConfirmToast({
            title: 'Aggiornamento disponibile',
            message: 'E\' disponibile una nuova versione. Clicca sul pulsante Aggiorna per aggiornare la pagina.',
            type: ToastMessageType.info
          }, 'Aggiorna', 'refresh').pipe(this.untilDestroyed()).subscribe(_ => window.location.reload());
        });
        break;
      case 'VERSION_INSTALLATION_FAILED':
        LogService.debug(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
        break;
    }
    return Promise.resolve();
  }

  protected abstract untilDestroyed(): <U>(source: Observable<U>) => Observable<U>;

  async checkStatus(): Promise<StatusMessageInterface> {
    return Promise.resolve({
      hasModalOpen: false,
      pendingChanges: false,
      version: 1
    });
  }
}
